/**
 * Amplify GraphQL Subscriptions Service
 * 
 * Uses AWS Amplify's built-in subscription API for real-time updates.
 * This handles WebSocket authentication automatically using Cognito User Pool.
 * 
 * When data changes on the server (via any client), all connected clients
 * receive the update automatically.
 */

import { generateClient } from 'aws-amplify/api';
import { loggers } from '../utils/logger';

// Type helper for Amplify GraphQL subscriptions
type GraphQLSubscription = {
  subscribe: (handlers: {
    next: (value: { data: Record<string, unknown> }) => void;
    error: (error: unknown) => void;
  }) => { unsubscribe: () => void };
};

// Connection state type
export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

// Subscription callback types
export interface SubscriptionCallbacks {
  onLessonUpdate?: (lesson: any) => void;
  onLessonSeriesUpdate?: (series: any) => void;
  onStudentUpdate?: (student: any) => void;
  onFamilyUpdate?: (family: any) => void;
  onLocationUpdate?: (location: any) => void;
  onInvoiceUpdate?: (invoice: any) => void;
  onError?: (error: any) => void;
  onConnectionStateChange?: (state: ConnectionState) => void;
}

// GraphQL subscription queries (matching backend schema.graphql exactly)
const SUBSCRIPTIONS = {
  onLessonUpdated: /* GraphQL */ `
    subscription OnLessonUpdated($tutorId: ID!) {
      onLessonUpdated(tutorId: $tutorId) {
        id
        tutorId
        studentId
        familyId
        locationId
        subject
        topic
        time
        duration
        date
        status
        hourlyRate
        lessonFee
        cancellationPolicy
        notes
        homework
        pieces
        recurring
        parentRecurringLessonId
        seriesId
        seriesIndex
        cancellationReason
        cancellationComment
        cancelledAt
        policyImplication
        createdAt
        updatedAt
      }
    }
  `,
  onLessonSeriesUpdated: /* GraphQL */ `
    subscription OnLessonSeriesUpdated($tutorId: ID!) {
      onLessonSeriesUpdated(tutorId: $tutorId) {
        id
        tutorId
        studentId
        familyId
        locationId
        subject
        time
        duration
        dayOfWeek
        dayOfWeekNum
        startDate
        endDate
        recurringEndDateEventId
        customLessonFee
        notes
        status
        createdAt
        updatedAt
      }
    }
  `,
  onStudentUpdated: /* GraphQL */ `
    subscription OnStudentUpdated($tutorId: ID!) {
      onStudentUpdated(tutorId: $tutorId) {
        id
        tutorId
        firstName
        lastName
        email
        phone
        dateOfBirth
        emergencyContact
        emergencyPhone
        notes
        subject
        familyId
        status
        studentType
        instruments
        createdAt
        updatedAt
      }
    }
  `,
  onLocationUpdated: /* GraphQL */ `
    subscription OnLocationUpdated($tutorId: ID!) {
      onLocationUpdated(tutorId: $tutorId) {
        id
        tutorId
        name
        address
        hourlyRate
        cancellationPolicy
        notes
        status
        locationPreset
        createdAt
        updatedAt
      }
    }
  `,
  onFamilyUpdated: /* GraphQL */ `
    subscription OnFamilyUpdated($tutorId: ID!) {
      onFamilyUpdated(tutorId: $tutorId) {
        id
        tutorId
        familyName
        address
        phone
        email
        notes
        createdAt
        updatedAt
      }
    }
  `,
  onInvoiceUpdated: /* GraphQL */ `
    subscription OnInvoiceUpdated($tutorId: ID!) {
      onInvoiceUpdated(tutorId: $tutorId) {
        id
        tutorId
        studentId
        familyId
        invoiceNumber
        amount
        totalAmount
        status
        dueDate
        paidDate
        notes
        createdAt
        updatedAt
      }
    }
  `,
};

interface ActiveSubscription {
  unsubscribe: () => void;
  name: string;
}

/**
 * Amplify Subscription Manager
 * 
 * Manages real-time GraphQL subscriptions using AWS Amplify's built-in API.
 * Uses singleton pattern to ensure only one set of subscriptions is active.
 */
class AmplifySubscriptionManager {
  private static instance: AmplifySubscriptionManager;
  private subscriptions: ActiveSubscription[] = [];
  private callbacks: SubscriptionCallbacks = {};
  private tutorId: string | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private isInitializing = false;
  private client: any = null;

  private constructor() {}

  static getInstance(): AmplifySubscriptionManager {
    if (!AmplifySubscriptionManager.instance) {
      AmplifySubscriptionManager.instance = new AmplifySubscriptionManager();
    }
    return AmplifySubscriptionManager.instance;
  }

  /**
   * Set callbacks for subscription updates
   */
  setCallbacks(callbacks: SubscriptionCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      loggers.subscriptions.log('Connection state changed:', state);
      this.callbacks.onConnectionStateChange?.(state);
    }
  }

  /**
   * Initialize subscriptions for a tutor
   */
  async initialize(tutorId: string): Promise<void> {
    if (this.isInitializing) {
      loggers.subscriptions.log('Already initializing, skipping...');
      return;
    }

    if (this.tutorId === tutorId && this.connectionState === 'connected') {
      loggers.subscriptions.log('Already connected for this tutor');
      return;
    }

    // Clean up any existing subscriptions first
    if (this.subscriptions.length > 0) {
      this.disconnect();
    }

    this.isInitializing = true;
    this.tutorId = tutorId;
    this.setConnectionState('connecting');

    try {
      loggers.subscriptions.log('Initializing Amplify subscriptions for tutor:', tutorId);
      
      // Create Amplify GraphQL client
      this.client = generateClient();

      // Subscribe to all relevant events
      this.subscribeToLessons(tutorId);
      this.subscribeToLessonSeries(tutorId);
      this.subscribeToStudents(tutorId);
      this.subscribeToLocations(tutorId);
      this.subscribeToFamilies(tutorId);
      this.subscribeToInvoices(tutorId);

      this.setConnectionState('connected');
      loggers.subscriptions.log('Successfully initialized all subscriptions');
    } catch (error) {
      loggers.subscriptions.error('Failed to initialize subscriptions:', error);
      this.setConnectionState('error');
      this.callbacks.onError?.(error);
    } finally {
      this.isInitializing = false;
    }
  }

  private subscribeToLessons(tutorId: string): void {
    if (!this.client) return;

    try {
      const subscription = (this.client.graphql({
        query: SUBSCRIPTIONS.onLessonUpdated,
        variables: { tutorId }
      }) as unknown as GraphQLSubscription).subscribe({
        next: ({ data }: { data: Record<string, unknown> }) => {
          const lesson = (data as any)?.onLessonUpdated;
          if (lesson) {
            loggers.subscriptions.log('Lesson updated:', lesson.id, lesson.status);
            this.callbacks.onLessonUpdate?.(lesson);
          }
        },
        error: (error: any) => {
          loggers.subscriptions.error('Lesson subscription error:', error);
          this.handleSubscriptionError(error);
        }
      });

      this.subscriptions.push({ unsubscribe: () => subscription.unsubscribe(), name: 'lessons' });
      loggers.subscriptions.log('Subscribed to lesson updates');
    } catch (error) {
      loggers.subscriptions.error('Failed to subscribe to lessons:', error);
    }
  }

  private subscribeToLessonSeries(tutorId: string): void {
    if (!this.client) return;

    try {
      const subscription = (this.client.graphql({
        query: SUBSCRIPTIONS.onLessonSeriesUpdated,
        variables: { tutorId }
      }) as unknown as GraphQLSubscription).subscribe({
        next: ({ data }: { data: Record<string, unknown> }) => {
          const series = (data as any)?.onLessonSeriesUpdated;
          if (series) {
            loggers.subscriptions.log('Series updated:', series.id);
            this.callbacks.onLessonSeriesUpdate?.(series);
          }
        },
        error: (error: any) => {
          loggers.subscriptions.error('Series subscription error:', error);
          this.handleSubscriptionError(error);
        }
      });

      this.subscriptions.push({ unsubscribe: () => subscription.unsubscribe(), name: 'series' });
      loggers.subscriptions.log('Subscribed to series updates');
    } catch (error) {
      loggers.subscriptions.error('Failed to subscribe to series:', error);
    }
  }

  private subscribeToStudents(tutorId: string): void {
    if (!this.client) return;

    try {
      const subscription = (this.client.graphql({
        query: SUBSCRIPTIONS.onStudentUpdated,
        variables: { tutorId }
      }) as unknown as GraphQLSubscription).subscribe({
        next: ({ data }: { data: Record<string, unknown> }) => {
          const student = (data as any)?.onStudentUpdated;
          if (student) {
            loggers.subscriptions.log('Student updated:', student.id);
            this.callbacks.onStudentUpdate?.(student);
          }
        },
        error: (error: any) => {
          loggers.subscriptions.error('Student subscription error:', error);
          this.handleSubscriptionError(error);
        }
      });

      this.subscriptions.push({ unsubscribe: () => subscription.unsubscribe(), name: 'students' });
      loggers.subscriptions.log('Subscribed to student updates');
    } catch (error) {
      loggers.subscriptions.error('Failed to subscribe to students:', error);
    }
  }

  private subscribeToLocations(tutorId: string): void {
    if (!this.client) return;

    try {
      const subscription = (this.client.graphql({
        query: SUBSCRIPTIONS.onLocationUpdated,
        variables: { tutorId }
      }) as unknown as GraphQLSubscription).subscribe({
        next: ({ data }: { data: Record<string, unknown> }) => {
          const location = (data as any)?.onLocationUpdated;
          if (location) {
            loggers.subscriptions.log('Location updated:', location.id);
            this.callbacks.onLocationUpdate?.(location);
          }
        },
        error: (error: any) => {
          loggers.subscriptions.error('Location subscription error:', error);
          this.handleSubscriptionError(error);
        }
      });

      this.subscriptions.push({ unsubscribe: () => subscription.unsubscribe(), name: 'locations' });
      loggers.subscriptions.log('Subscribed to location updates');
    } catch (error) {
      loggers.subscriptions.error('Failed to subscribe to locations:', error);
    }
  }

  private subscribeToFamilies(tutorId: string): void {
    if (!this.client) return;

    try {
      const subscription = (this.client.graphql({
        query: SUBSCRIPTIONS.onFamilyUpdated,
        variables: { tutorId }
      }) as unknown as GraphQLSubscription).subscribe({
        next: ({ data }: { data: Record<string, unknown> }) => {
          const family = (data as any)?.onFamilyUpdated;
          if (family) {
            loggers.subscriptions.log('Family updated:', family.id);
            this.callbacks.onFamilyUpdate?.(family);
          }
        },
        error: (error: any) => {
          loggers.subscriptions.error('Family subscription error:', error);
          this.handleSubscriptionError(error);
        }
      });

      this.subscriptions.push({ unsubscribe: () => subscription.unsubscribe(), name: 'families' });
      loggers.subscriptions.log('Subscribed to family updates');
    } catch (error) {
      loggers.subscriptions.error('Failed to subscribe to families:', error);
    }
  }

  private subscribeToInvoices(tutorId: string): void {
    if (!this.client) return;

    try {
      const subscription = (this.client.graphql({
        query: SUBSCRIPTIONS.onInvoiceUpdated,
        variables: { tutorId }
      }) as unknown as GraphQLSubscription).subscribe({
        next: ({ data }: { data: Record<string, unknown> }) => {
          const invoice = (data as any)?.onInvoiceUpdated;
          if (invoice) {
            loggers.subscriptions.log('Invoice updated:', invoice.id);
            this.callbacks.onInvoiceUpdate?.(invoice);
          }
        },
        error: (error: any) => {
          loggers.subscriptions.error('Invoice subscription error:', error);
          this.handleSubscriptionError(error);
        }
      });

      this.subscriptions.push({ unsubscribe: () => subscription.unsubscribe(), name: 'invoices' });
      loggers.subscriptions.log('Subscribed to invoice updates');
    } catch (error) {
      loggers.subscriptions.error('Failed to subscribe to invoices:', error);
    }
  }

  private handleSubscriptionError(error: any): void {
    // Only set error state if we were connected
    if (this.connectionState === 'connected') {
      this.setConnectionState('error');
    }
    this.callbacks.onError?.(error);
  }

  /**
   * Disconnect and clean up all subscriptions
   */
  disconnect(): void {
    loggers.subscriptions.log('Disconnecting all subscriptions...');

    // Unsubscribe from all active subscriptions
    for (const sub of this.subscriptions) {
      try {
        sub.unsubscribe();
        loggers.subscriptions.log(`Unsubscribed from ${sub.name}`);
      } catch (error) {
        loggers.subscriptions.error(`Error unsubscribing from ${sub.name}:`, error);
      }
    }

    this.subscriptions = [];
    this.tutorId = null;
    this.client = null;
    this.setConnectionState('disconnected');
  }

  /**
   * Force refresh - disconnect and reinitialize
   */
  async refresh(): Promise<void> {
    if (this.tutorId) {
      const tutorId = this.tutorId;
      this.disconnect();
      await this.initialize(tutorId);
    }
  }
}

// Export singleton instance
export const amplifySubscriptionManager = AmplifySubscriptionManager.getInstance();
